/**
 * S3 Data Fetcher API
 *
 * データソースの切り替え:
 * - 公開バケット（lecture.json, notice.json）:
 *   S3から取得を試みる。開発環境でS3が利用不可の場合はローカルデータにフォールバック。
 *   本番環境ではS3取得失敗時にエラーをスロー。
 * - 問題データ（questions）:
 *   WebSocket (AppSync Events) 経由で管理者が出題するたびにリアルタイム配信。
 *   途中参加者は REST API (GET /questions/current) で取得。
 * - チャット設定・NGデータ（config, ng_users, ng_words）:
 *   開発時はローカルモックデータを使用。本番ではデフォルト値を返す。
 */

import axios, { AxiosError } from 'axios';
import type {
  LectureData,
  NoticeData,
  QuestionsData,
  ChatConfig,
  NgUsersData,
  NgWordsData,
} from '../types';
import {
  buildLectureUrl,
  buildNoticeUrl,
} from '../utils/s3UrlBuilder';
import {
  isLocalDataEnabled,
  getLocalLecture,
  getLocalNotice,
  getLocalChatConfig,
  getLocalNgUsers,
  getLocalNgWords,
} from './localDataProvider';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms

/**
 * localStorage に問題データを保存する
 */
export function saveQuestionsToLocalStorage(
  tenantSlug: string,
  lectureId: string,
  questionsData: QuestionsData
): void {
  try {
    const key = `elive_questions_${tenantSlug}_${lectureId}`;
    localStorage.setItem(key, JSON.stringify(questionsData));
    console.log('[localStorage] questions 保存完了:', questionsData.questions.length, '問');
  } catch (e) {
    console.warn('[localStorage] questions 保存エラー:', e);
  }
}

/**
 * リトライ付きフェッチ（公開バケット用）
 */
async function fetchWithRetry<T>(
  fetcher: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = RETRY_DELAY
): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fetcher();
    } catch (error) {
      if (i === retries - 1) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
    }
  }
  throw new Error('Max retries reached');
}

function handleFetchError(error: unknown, context: string): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      throw new Error(
        `${context}: HTTP ${axiosError.response.status} - ${axiosError.response.statusText}`
      );
    } else if (axiosError.request) {
      throw new Error(`${context}: ネットワークエラー - サーバーに接続できません`);
    }
  }
  throw new Error(`${context}: ${error instanceof Error ? error.message : '不明なエラー'}`);
}

/**
 * Fetch lecture.json from S3 (public bucket)
 * S3から取得を試み、開発環境でS3が利用不可の場合はローカルデータにフォールバック
 * @param tenantSlug - Tenant URL slug
 * @param internalId - Lecture internal ID (string, used for S3 path)
 * @param isPreview - Whether to fetch from preview path
 * @returns Lecture data
 */
export async function fetchLecture(
  tenantSlug: string,
  internalId: string,
  isPreview: boolean = false
): Promise<LectureData> {
  try {
    const url = buildLectureUrl(tenantSlug, internalId, isPreview);
    return await fetchWithRetry(async () => {
      const response = await axios.get<LectureData>(url);
      return response.data;
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(
        '[S3 Fallback] lecture.json の S3 取得に失敗しました。ローカルデータを使用します。',
        error
      );
      return getLocalLecture(tenantSlug, internalId, isPreview);
    }
    handleFetchError(error, 'lecture.jsonの取得に失敗しました');
  }
}

/**
 * Fetch notice.json from S3 (public bucket)
 * S3から取得を試み、開発環境でS3が利用不可の場合はローカルデータにフォールバック
 * @param tenantSlug - Tenant URL slug
 * @param isPreview - Whether to fetch from preview path
 * @returns Notice data
 */
export async function fetchNotice(
  tenantSlug: string,
  isPreview: boolean = false
): Promise<NoticeData> {
  try {
    const url = buildNoticeUrl(tenantSlug, isPreview);
    return await fetchWithRetry(async () => {
      const response = await axios.get<NoticeData>(url);
      return response.data;
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(
        '[S3 Fallback] notice.json の S3 取得に失敗しました。ローカルデータを使用します。',
        error
      );
      return getLocalNotice(tenantSlug, isPreview);
    }
    // notice.json は任意データのため、取得失敗時は空データを返して画面表示を継続する
    console.warn('notice.json の取得に失敗しました。お知らせなしで続行します。', error);
    return {
      meta: {
        generated_at: new Date().toISOString(),
        version: '1.0',
        tenant: { url_slug: tenantSlug },
      },
      notice: '',
    };
  }
}

/**
 * 問題データを取得する。
 *
 * 1. localStorage から復元（WebSocket で受信した問題データを同一講義内で保持）
 * 2. 無ければ空配列（初回ロード後は WebSocket / GET /questions/current・/questions/ended で補完）
 *
 * @param lectureStatus - 講義のステータス（ready/running/ended）。readyの場合は問題をnot_startedにリセット
 */
export async function fetchQuestions(
  tenantSlug: string,
  lectureId: string,
  lectureStatus?: 'ready' | 'running' | 'ended'
): Promise<QuestionsData> {
  const emptyData: QuestionsData = {
    meta: {
      generated_at: new Date().toISOString(),
      version: '1.0',
      tenant: { url_slug: tenantSlug },
      lecture: { id: 0, name: '' },
    },
    questions: [],
  };

  let questionsData: QuestionsData | null = null;

  try {
    const storageKey = `elive_questions_${tenantSlug}_${lectureId}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as QuestionsData;
      if (parsed?.questions?.length > 0) {
        console.log('[localStorage] questions 復元:', parsed.questions.length, '問');
        
        // 古い形式（question_images: string[]）を新しい形式（Array<{url, label}>）に変換
        const normalizedQuestions = parsed.questions.map(q => {
          if (q.question_images && Array.isArray(q.question_images) && q.question_images.length > 0) {
            const firstItem = q.question_images[0];
            // 文字列配列の場合はオブジェクト配列に変換
            if (typeof firstItem === 'string') {
              return {
                ...q,
                question_images: (q.question_images as unknown as string[]).map(url => ({ url }))
              };
            }
          }
          return q;
        });
        
        questionsData = {
          ...parsed,
          questions: normalizedQuestions
        };
      }
    }
  } catch (e) {
    console.warn('[localStorage] questions 復元エラー:', e);
  }

  // 講義ステータスに応じて問題ステータスを調整
  if (questionsData) {
    if (lectureStatus === 'ready') {
      // 講義前: 全問題をnot_startedにリセット
      console.log('[fetchQuestions] 講義ステータスが「講義前」のため、全問題をnot_startedにリセット');
      questionsData = {
        ...questionsData,
        questions: questionsData.questions.map(q => ({
          ...q,
          status: 'not_started' as const,
        })),
      };
    } else if (lectureStatus === 'ended') {
      // 講義終了: answering/tallying状態の問題をcompletedに変更（結果表示のため）
      console.log('[fetchQuestions] 講義ステータスが「講義終了」のため、answering/tallying状態の問題をcompletedにリセット');
      questionsData = {
        ...questionsData,
        questions: questionsData.questions.map(q => {
          if (q.status === 'answering' || q.status === 'tallying') {
            return { ...q, status: 'completed' as const };
          }
          return q;
        }),
      };
    }
  }

  return questionsData ?? emptyData;
}

/**
 * チャット設定を返す。
 * 開発環境ではローカルモックデータを使用し、本番ではデフォルト設定を返す。
 */
export async function fetchChatConfig(
  tenantSlug: string,
  lectureId: string
): Promise<ChatConfig> {
  if (isLocalDataEnabled()) {
    return getLocalChatConfig(tenantSlug, lectureId);
  }

  return {
    meta: {
      generated_at: new Date().toISOString(),
      version: '1.0',
      tenant: { url_slug: tenantSlug },
      lecture: { id: 0 },
    },
  };
}

/**
 * NGユーザーリストを返す。
 * 開発環境ではローカルモックデータを使用し、本番では空リストを返す。
 */
export async function fetchNgUsers(tenantSlug: string): Promise<NgUsersData> {
  if (isLocalDataEnabled()) {
    return getLocalNgUsers(tenantSlug);
  }

  return {
    meta: {
      generated_at: new Date().toISOString(),
      version: '1.0',
      tenant: { url_slug: tenantSlug },
    },
    ngUsers: [],
  };
}

/**
 * NGワードリストを返す。
 * 開発環境ではローカルモックデータを使用し、本番では空リストを返す。
 */
export async function fetchNgWords(tenantSlug: string): Promise<NgWordsData> {
  if (isLocalDataEnabled()) {
    return getLocalNgWords(tenantSlug);
  }

  return {
    meta: {
      generated_at: new Date().toISOString(),
      version: '1.0',
      tenant: { url_slug: tenantSlug },
    },
    ngWords: [],
  };
}

/**
 * Fetch all data in parallel
 * 必須: lecture.json のみ。取得失敗時はエラーをスローしページ表示をブロックする。
 * 任意: notice / questions / chatConfig / ngUsers / ngWords は取得失敗時に空データで続行する。
 * @param tenantSlug - Tenant URL slug
 * @param internalId - Lecture internal ID (string, used for S3 path)
 * @param isPreview - Whether to fetch from preview path
 * @returns All data
 */
export async function fetchAllData(
  tenantSlug: string,
  internalId: string,
  isPreview: boolean = false
): Promise<{
  lecture: LectureData;
  notice: NoticeData;
  questions: QuestionsData;
  chatConfig: ChatConfig;
  ngUsers: NgUsersData;
  ngWords: NgWordsData;
}> {
  // lecture.json を先に取得して講義ステータスを確認
  const lecture = await fetchLecture(tenantSlug, internalId, isPreview);
  const lectureStatus = lecture.live?.status;
  const lectureIdStr = lecture.internal_id ?? internalId;

  // 他のデータは各関数内で失敗時に空データを返す
  const [notice, questions, chatConfig, ngUsers, ngWords] = await Promise.all([
    fetchNotice(tenantSlug, isPreview),
    fetchQuestions(tenantSlug, lectureIdStr, lectureStatus),
    fetchChatConfig(tenantSlug, lectureIdStr),
    fetchNgUsers(tenantSlug),
    fetchNgWords(tenantSlug),
  ]);

  return {
    lecture,
    notice,
    questions,
    chatConfig,
    ngUsers,
    ngWords,
  };
}
